import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import { orderBy } from 'lodash-es';
import red from 'sushiweb/tokens/color/red';
import Layout from 'sushiweb/Layout/Layout';
import Spinner from 'sushiweb/Spinner/Spinner';
import Search from 'sushiweb/Icons/all/Search';
import Download from 'sushiweb/Icons/all/Download';
import Breadcrumb from 'sushiweb/Breadcrumb/Breadcrumb';
import H5 from 'sushiweb/Typography/Heading/H5';

import HorizontalTabs from 'sushiweb/HorizontalTabs';
import DataTable from '../common/components/DataTables/DataTables';
import ErrorPage from '../common/components/ErrorPage';
import {
  DashboardWrapper,
  HeadWrapper,
  SpinnerHolder,
  LayoutContainers,
  TableContainer,
  TabWrapper,
  SearchContainerV2,
  SearchInput,
  BreadCrumbWrapper,
  StatusFilterAndExportCSVContainer,
  ConsumptionHeadWrapper,
  TabContainer,
  tabStyle,
} from '../common/styles';
import {
  REFRESH_LABEL,
  applicableFlowTypes,
  businessTypeEnum,
  creditTransactionsStatusEnum,
  currentFlowTypeEnum,
  employeeStatusEnum,
  statusLabels,
} from '../common/components/constants';
import { creditTransactionsForEmployeeHeadColumn } from '../common/components/DataTables/components/headColumns';
import { StatusContainer } from '../common/components/DataTables/styles';
import {
  exportCSV,
  formatAmountInRupees,
  formatDateinDDMMMYYY,
  getCurrentDate,
  includesOption,
  indexOfOption,
  searchWithName,
} from '../../helpers';
import DetailsWrapper from '../common/components/DetailsWrapper/DetailsWrapper';
import StatusFilter from '../common/components/StatusFilter';
import { DASHBOARD_ROUTE, EMPLOYEES_ROUTE, HOME_ROUTE } from '../../constants/routes';
import EmployeeConsumptions from './EmployeeConsumptions';
import ZTrackingClient from '../../jumbo/ZTrackingClient';
import { NewTagDiv } from '../common/components/SideNavbar/styles';

const EmployeeDetails = ({
  currentFlow,
  groupDetails,
  employeeDetails,
  creditTransactions,
  getEmployeeDetails,
  fetchingEmployeeDetails,
  getEmployeeCreditTransactions,
  fetchingCreditTransactions,
  fetchedCreditTransactions,
}) => {
  const keyFields = ['program_name', 'created_on'];
  const [currentPage, setCurrentPage] = useState(1);
  const [tableData, setTableData] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const { emailHash } = useParams();
  const [search, onInputChange] = useState('');
  const [typingTimeout, setTypingTimeout] = useState(0);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentFilters, setCurrentFilters] = useState([]);
  const [isSearchClicked, setIsSearchClicked] = useState(false);
  const [disabledSearch, setDisabledSearch] = useState(false);
  const [consumptionActiveIndex, setConsumptionActiveIndex] = useState(0);
  const [businessType, setBusinessType] = useState(
    businessTypeEnum.BUSINESS_TYPE_ONLINE_ORDERING,
  );
  const usageDetails =
    employeeDetails?.benefit_details?.limit?.applied_program?.usage_details;
  const hasBusinessTypeDining =
    usageDetails && businessTypeEnum.BUSINESS_TYPE_DINING in usageDetails;
  const [filterStatusOptions, setFilterStatusOptions] = useState([
    {
      value: [1],
      label: 'GENERATED_SCHEDULED',
      disabled: true,
    },
    {
      value: [2, 6, 8],
      label: 'INITIATED_PROCESSING',
      disabled: true,
    },
    {
      value: [3],
      label: 'WALLET_CREATION_PENDING_ONBOARDING_PENDING',
      disabled: true,
    },
    {
      value: [4],
      label: 'SUCCESS',
      disabled: true,
    },
    {
      value: [5, 7],
      label: 'FAILED',
      disabled: true,
    },
  ]);
  const filterFunc = val => {
    let flag = false;
    currentFilters.forEach(filter => {
      for (let i = 0; i < filter.value.length; i += 1)
        if (val.status === filter.value[i]) flag = true;
    });
    return flag;
  };

  const enableCurrentFilters = availableStatuses => {
    for (let i = 0; i < filterStatusOptions.length; i += 1) {
      for (let j = 0; j < filterStatusOptions[i].value.length; j += 1) {
        if (availableStatuses.includes(filterStatusOptions[i].value[j])) {
          filterStatusOptions[i].disabled = false;
          break;
        } else filterStatusOptions[i].disabled = true;
      }
    }
    setFilterStatusOptions(filterStatusOptions);
  };

  const handleConsumptionTabsClick = val => () => {
    setConsumptionActiveIndex(val);
    setBusinessType(
      val === 0
        ? businessTypeEnum.BUSINESS_TYPE_ONLINE_ORDERING
        : businessTypeEnum.BUSINESS_TYPE_DINING,
    );
  };

  useEffect(() => {
    getEmployeeDetails({ emailHash, flowType: currentFlow });
  }, [currentFlow]);

  useEffect(() => {
    ZTrackingClient.recordImpression({
      ui_source: 'EMPLOYEE_DETAILS',
      component: 'TABLE_DATA',
    });
  }, []);

  useEffect(() => {
    if (currentFlow !== currentFlowTypeEnum.FLOW_TYPE_LIMIT)
      getEmployeeCreditTransactions(emailHash);
  }, [currentFlow]);

  useEffect(() => {
    if (!fetchingCreditTransactions && fetchedCreditTransactions) {
      if (creditTransactions.length) {
        creditTransactions.sort(
          (a, b) => new Date(b.created_on) - new Date(a.created_on),
        );
        if (creditTransactions.length === 0) {
          setDisabledSearch(true);
        }
        setTableData(creditTransactions);
        setCurrentData(creditTransactions.slice(0, 10));
      }
      setLoading(false);
    }
  }, [fetchingCreditTransactions]);

  useEffect(() => {
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    const timeout = setTimeout(() => {
      if (!fetchingCreditTransactions) {
        const arr = searchWithName(creditTransactions, search, keyFields);
        const availableStatuses = [];
        arr.forEach(data => {
          if (!availableStatuses.includes(data.status)) {
            availableStatuses.push(data.status);
          }
        });
        enableCurrentFilters(availableStatuses);
        if (currentFilters.length !== 0) {
          const filteredSearchArr = arr.filter(filterFunc);
          setSearchResults(filteredSearchArr);
        } else {
          setSearchResults(arr);
        }
        handlePageChange(1);
      }
    }, 500);

    return () => setTypingTimeout(timeout);
  }, [search, currentFilters, fetchingCreditTransactions]);

  useEffect(() => {
    if (searchResults) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = searchResults.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage, searchResults]);

  useEffect(() => {
    if (tableData.length > 0) {
      const start = currentPage * 10 - 10;
      const end = currentPage * 10;
      const rangeData = tableData.slice(start, end);
      setCurrentData(rangeData);
    }
  }, [currentPage]);

  const handlePageChange = page => {
    setCurrentPage(page);
  };

  const handleSort = (column, sortDirection) => {
    setSearchResults(orderBy(searchResults, column.selector, sortDirection));
  };

  const refreshPage = () => {
    getEmployeeDetails({ emailHash, flowType: currentFlow });
    if (currentFlow !== currentFlowTypeEnum.FLOW_TYPE_LIMIT)
      getEmployeeCreditTransactions(emailHash);
  };

  const exportSearchResultCSV = () => {
    const headColumns = [
      { key: 'program_name', value: 'Meal Plan' },
      { key: 'created_on', value: 'Created On' },
      { key: 'req_amt', value: 'Amount required' },
      { key: 'actual_amt', value: 'Actual Amount' },
      { key: 'status', value: 'Status' },
    ];
    const statusLabelledData = [];
    searchResults.forEach(data => {
      const labelledData = {};
      labelledData.program_name = data.program_name;
      labelledData.created_on = formatDateinDDMMMYYY(data.created_on);
      labelledData.actual_amt = formatAmountInRupees(String(data.actual_amt));
      labelledData.req_amt = formatAmountInRupees(String(data.req_amt));
      labelledData.status =
        statusLabels[creditTransactionsStatusEnum[data.status]];
      statusLabelledData.push(labelledData);
    });
    exportCSV(
      statusLabelledData,
      `${employeeDetails.email} - Topups - ${getCurrentDate()}`,
      headColumns,
    );
  };
  const downloadCSVStyle = {
    paddingLeft: '5px',
    paddingRight: '20px',
    cursor: 'pointer',
  };
  const handleSelect = val => {
    const newCurrentFilters = [...currentFilters];
    if (includesOption(newCurrentFilters, val)) {
      const index = indexOfOption(newCurrentFilters, val);
      newCurrentFilters.splice(index, 1);
    } else {
      newCurrentFilters.push(val);
    }
    setCurrentFilters(newCurrentFilters);
  };
  const clearFilters = () => {
    setCurrentFilters([]);
  };

  return fetchingEmployeeDetails ? (
    <DashboardWrapper>
      <SpinnerHolder className='center'>
        <Spinner color={red.z_red} size='medium' />
      </SpinnerHolder>
    </DashboardWrapper>
  ) : (
    <DashboardWrapper>
      <BreadCrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}#${HOME_ROUTE}`}
          >
            Home
          </Breadcrumb.Link>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}#${EMPLOYEES_ROUTE}`}
          >
            Employees
          </Breadcrumb.Link>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-800, #414A5D)' }}
          >
            Employee
          </Breadcrumb.Link>
        </Breadcrumb>
      </BreadCrumbWrapper>
      <HeadWrapper style={{ marginTop: '20px' }}>
        <HeadWrapper.Left>
          <H5> 
            {' '}
            {employeeDetails.email ?? ' '}
            {' '}
          </H5>
          {currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT ? (
            <StatusContainer
              style={{ marginLeft: '10px' }}
              status={employeeStatusEnum[employeeDetails.limit_status]}
            >
              {statusLabels[employeeStatusEnum[employeeDetails.limit_status]]}
            </StatusContainer>
          ) : (
            <StatusContainer
              style={{ marginLeft: '10px' }}
              status={employeeStatusEnum[employeeDetails.status]}
            >
              {statusLabels[employeeStatusEnum[employeeDetails.status]]}
            </StatusContainer>
          )}
        </HeadWrapper.Left>
      </HeadWrapper>
      <LayoutContainers>
        <Layout gap={20}>
          <Layout.Item colD={4} colT={4} colM={4}>
            <DetailsWrapper
              label='Team name'
              value={
                groupDetails?.group_name !== ''
                  ? groupDetails?.group_name
                  : ' - '
              }
            />
          </Layout.Item>
          <Layout.Item colD={4} colT={4} colM={4}>
            <DetailsWrapper
              label='Meal plan'
              value={
                groupDetails?.program_name && groupDetails?.program_name !== ''
                  ? groupDetails?.program_name
                  : ' - '
              }
            />
          </Layout.Item>
        </Layout>
      </LayoutContainers>
      {currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT ? (
        <ConsumptionHeadWrapper>
          <H5>Consumption details</H5>
        </ConsumptionHeadWrapper>
      ) : (
        <HeadWrapper>
          <H5>Past and Upcoming Topups</H5>
        </HeadWrapper>
      )}
      {currentFlow === currentFlowTypeEnum.FLOW_TYPE_LIMIT ? (
        <TabWrapper>
          {hasBusinessTypeDining ? (
            <TabContainer>
              <HorizontalTabs
                style={{ marginBottom: '-4.5px' }}
                alignLinks='left'
                additionalGap='1.8rem'
              >
                <HorizontalTabs.Link
                  active={consumptionActiveIndex === 0}
                  onClick={handleConsumptionTabsClick(0)}
                  style={{
                    width: '140px',
                    color:
                      consumptionActiveIndex === 0
                        ? 'var(--coolgreycoolgrey-800, #414A5D)'
                        : 'var(--coolgreycoolgrey-400, #AFB4C0)',
                    ...tabStyle,
                  }}
                >
                  Delivery
                </HorizontalTabs.Link>
                <HorizontalTabs.Link
                  active={consumptionActiveIndex === 1}
                  onClick={handleConsumptionTabsClick(1)}
                  style={{
                    width: '200px',
                    color:
                      consumptionActiveIndex === 1
                        ? 'var(--coolgreycoolgrey-800, #414A5D)'
                        : 'var(--coolgreycoolgrey-400, #AFB4C0)',
                    ...tabStyle,
                  }}
                >
                  Dining 
                  {' '}
                  <NewTagDiv>NEW</NewTagDiv>
                </HorizontalTabs.Link>
              </HorizontalTabs>
            </TabContainer>
          ) : (
            <></>
          )}
          <EmployeeConsumptions
            employeeData={employeeDetails}
            selectedBusinessType={businessType}
          />
        </TabWrapper>
      ) : (
        <TabWrapper>
          {!fetchedCreditTransactions ? (
            <ErrorPage
              showAction
              actionLabel={REFRESH_LABEL}
              clickAction={refreshPage}
            />
          ) : (
            <>
              <SearchContainerV2>
                <SearchInput
                  leftIcon={<Search />}
                  innerWidth='100%'
                  value={search}
                  onChange={onInputChange}
                  onFocus={() => setIsSearchClicked(true)}
                  onBlur={() => setIsSearchClicked(false)}
                  label={
                    isSearchClicked || disabledSearch || search?.length ? (
                      'Search by meal plan or created on date'
                    ) : (
                      <span style={{ fontSize: '16px', fontWeight: 300 }}>
                        {' '}
                        Search by meal plan or created on date
                        {' '}
                      </span>
                    )
                  }
                  disabled={disabledSearch}
                />
                <StatusFilterAndExportCSVContainer>
                  <StatusFilter
                    isDisabled={disabledSearch}
                    clearFilters={clearFilters}
                    currentFilters={currentFilters}
                    options={filterStatusOptions}
                    handleSelect={handleSelect}
                  />
                  {disabledSearch ? (
                    <Download color='grey' style={downloadCSVStyle} />
                  ) : (
                    <Download
                      color='grey'
                      onClick={exportSearchResultCSV}
                      style={downloadCSVStyle}
                    />
                  )}
                </StatusFilterAndExportCSVContainer>
              </SearchContainerV2>
              <TableContainer>
                <DataTable
                  columns={creditTransactionsForEmployeeHeadColumn}
                  loading={fetchingCreditTransactions || loading}
                  data={currentData}
                  pagination
                  totalRows={searchResults ? searchResults.length : 0}
                  handlePageChange={handlePageChange}
                  onSort={handleSort}
                  emptyStateTitle='No past & upcoming top-ups'
                />
              </TableContainer>
            </>
          )}
        </TabWrapper>
      )}
    </DashboardWrapper>
  );
};

EmployeeDetails.propTypes = {
  currentFlow: PropTypes.oneOf(applicableFlowTypes),
  groupDetails: PropTypes.objectOf(PropTypes.any),
  employeeDetails: PropTypes.objectOf(PropTypes.any),
  creditTransactions: PropTypes.objectOf(PropTypes.any),
  getEmployeeDetails: PropTypes.func,
  fetchingEmployeeDetails: PropTypes.bool,
  getEmployeeCreditTransactions: PropTypes.func,
  fetchingCreditTransactions: PropTypes.bool,
  fetchedCreditTransactions: PropTypes.bool,
};

EmployeeDetails.defaultProps = {
  currentFlow: currentFlowTypeEnum.FLOW_TYPE_PPI,
  groupDetails: {},
  employeeDetails: {},
  creditTransactions: {},
  getEmployeeDetails: _noop,
  fetchingEmployeeDetails: false,
  getEmployeeCreditTransactions: _noop,
  fetchingCreditTransactions: false,
  fetchedCreditTransactions: false,
};

export default EmployeeDetails;
